/**
 * Unified Product Image System
 *
 * Single source of truth for product images across:
 * - Product detail pages (PDP carousel)
 * - Collection cards (product grid carousel)
 * - Cart drawer
 * - Hero carousels (homepage & collection pages)
 *
 * Uses content-map.json as the data source.
 *
 * Data format: `slides` arrays where each entry is either:
 *   - a string (image filename, e.g. "i-fm--01.jpg")
 *   - an object with type:"video" (e.g. { type:"video", src:"demo.mp4" })
 *
 * Resolution Strategy:
 * 1. collections.{occasion}.product_slides.{product} (occasion-specific overrides)
 * 2. products.{handle}.slides (product defaults)
 */

(function() {
  'use strict';

  // Cache for the loaded content map
  let contentMapCache = null;
  let contentMapPromise = null;

  // Cache for image existence checks (avoid repeated HEAD requests)
  const imageExistsCache = new Map();

  /**
   * Get the content map URL from the DOM.
   * Looks for #content-map-data first (rendered by theme.liquid),
   * then falls back to #content-map-url for legacy compatibility.
   * @returns {string|null} The URL or null if not found
   */
  function getContentMapUrl() {
    // Primary: #content-map-data (theme.liquid wraps the snippet output)
    const primaryEl = document.getElementById('content-map-data');
    if (primaryEl) {
      try { return JSON.parse(primaryEl.textContent.trim()); } catch (e) { /* fall through */ }
    }
    // Legacy fallback: #content-map-url
    const legacyEl = document.getElementById('content-map-url');
    if (legacyEl) {
      try { return JSON.parse(legacyEl.textContent.trim()); } catch (e) { /* fall through */ }
    }
    return null;
  }

  /**
   * Load and cache the content map JSON
   * Fetches from URL (preloaded in theme.liquid for performance)
   * @param {string} url - URL to content-map.json (optional, uses DOM if not provided)
   * @returns {Promise<Object>} The parsed content map
   */
  async function loadMediaMap(url) {
    if (contentMapCache) return contentMapCache;
    if (contentMapPromise) return contentMapPromise;

    // Get URL from DOM if not provided
    const fetchUrl = url || getContentMapUrl();
    if (!fetchUrl) {
      throw new Error('Content map URL not found');
    }

    contentMapPromise = fetch(fetchUrl, { credentials: 'same-origin' })
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load content map: ${res.status}`);
        return res.json();
      })
      .then(data => {
        contentMapCache = data;
        return data;
      });

    return contentMapPromise;
  }

  /**
   * Get the Shopify URL for a product
   * @param {Object} map - The content map
   * @param {string} productHandle - Product handle
   * @returns {string} The Shopify URL
   */
  function getProductUrl(map, productHandle) {
    return map?.products?.[productHandle]?.shopify_url || `/products/${productHandle}`;
  }

  /**
   * Get the abbreviation for a product handle
   * @param {Object} map - The content map
   * @param {string} handle - Product handle (e.g., "fridge-magnets", "photo-magnets")
   * @returns {string} The abbreviation (e.g., "fm", "pm")
   */
  function getAbbrev(map, handle) {
    return map?.products?.[handle]?.abbrev || '';
  }

  /**
   * Get product metadata from the map
   * @param {Object} map - The content map
   * @param {string} handle - Product handle
   * @returns {Object|null} Product metadata or null
   */
  function getProductMeta(map, handle) {
    return map?.products?.[handle] || null;
  }

  /**
   * Get collection metadata from the map
   * @param {Object} map - The content map
   * @param {string} collectionHandle - Collection handle (e.g., "mothers-day")
   * @returns {Object|null} Collection metadata or null
   */
  function getCollectionMeta(map, collectionHandle) {
    if (!collectionHandle) return null;
    return map?.collections?.[collectionHandle] || null;
  }

  /**
   * Extract only image filenames from a slides array.
   * Filters out video objects, returns only strings.
   * @param {Array} slides - Slides array (strings and/or objects)
   * @returns {string[]} Array of image filenames
   */
  function getImageFilenames(slides) {
    if (!Array.isArray(slides)) return [];
    return slides.filter(s => typeof s === 'string');
  }

  /**
   * Get occasion-specific image filenames for a product from a collection.
   * Reads from product_slides and filters to images only (no videos).
   * @param {Object} map - The content map
   * @param {string} collectionHandle - Collection handle
   * @param {string} productHandle - Product handle
   * @returns {string[]} Array of image filenames or empty array
   */
  function getCollectionProductImages(map, collectionHandle, productHandle) {
    if (map?.collections?.[collectionHandle]?.use_generic_images) return [];
    const slides = map?.collections?.[collectionHandle]?.product_slides?.[productHandle] || [];
    return getImageFilenames(slides);
  }

  /**
   * Build a full asset URL from a filename
   * @param {string} assetsBase - Base URL for assets
   * @param {string} filename - Image filename
   * @param {string} [version] - Cache-busting version from Shopify's asset_url ?v= param
   * @returns {string} Full URL
   */
  function buildAssetUrl(assetsBase, filename, version) {
    if (!filename) return '';
    if (/^https?:\/\//i.test(filename) || filename.startsWith('/')) return filename;
    var url = (assetsBase || '') + filename;
    if (version) url += (url.includes('?') ? '&' : '?') + 'v=' + version;
    return url;
  }

  /**
   * Build occasion-specific image filename
   * @param {string} occasion - Occasion handle (e.g., "mothers-day")
   * @param {string} abbrev - Product abbreviation (e.g., "fpm")
   * @param {number} slot - Image slot number (1-based)
   * @returns {string} Filename like "c-mothers-day-pm--01.jpg"
   */
  function buildOccasionImageFilename(occasion, abbrev, slot) {
    const paddedSlot = String(slot).padStart(2, '0');
    return `c-${occasion}-${abbrev}--${paddedSlot}.jpg`;
  }

  /**
   * Build generic image filename
   * @param {string} abbrev - Product abbreviation (e.g., "pm", "fm", "cal")
   * @param {number} slot - Image slot number (1-based)
   * @returns {string} Filename like "i-pm--01.jpg"
   */
  function buildGenericImageFilename(abbrev, slot) {
    const paddedSlot = String(slot).padStart(2, '0');
    return `i-${abbrev}--${paddedSlot}.jpg`;
  }

  /**
   * Test if an image URL exists by trying to load it
   * Results are cached to avoid repeated checks
   * @param {string} url - Image URL to test
   * @returns {Promise<boolean>} True if image loads successfully
   */
  function imageExists(url) {
    if (imageExistsCache.has(url)) {
      return Promise.resolve(imageExistsCache.get(url));
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        imageExistsCache.set(url, true);
        resolve(true);
      };
      img.onerror = () => {
        imageExistsCache.set(url, false);
        resolve(false);
      };
      img.src = url;
    });
  }

  /**
   * Resolve images for a product with occasion fallback (synchronous version)
   *
   * Reads from slides arrays, filtering out video entries.
   * Uses occasion product_slides first, falls back to product slides.
   *
   * @param {Object} map - The content map
   * @param {string} productHandle - Product handle
   * @param {string} occasion - Collection/occasion handle (optional)
   * @param {number} count - Number of images to return (default 3)
   * @param {string} assetsBase - Base URL for assets
   * @param {string} [version] - Cache-busting version string
   * @returns {string[]} Array of image URLs
   */
  function resolveImages(map, productHandle, occasion, count = 3, assetsBase = '', version) {
    const productMeta = getProductMeta(map, productHandle);
    if (!productMeta) return [];

    const defaultImages = getImageFilenames(productMeta.slides || []);

    // If no occasion, return default images
    if (!occasion) {
      return defaultImages
        .slice(0, count)
        .map(f => buildAssetUrl(assetsBase, f, version));
    }

    // Get occasion-specific images for this product from the collection
    const occasionImages = getCollectionProductImages(map, occasion, productHandle);

    // Build result: occasion images first, fill with default
    const result = [];

    for (let i = 0; i < count; i++) {
      if (i < occasionImages.length && occasionImages[i]) {
        result.push(buildAssetUrl(assetsBase, occasionImages[i], version));
      } else if (i < defaultImages.length) {
        result.push(buildAssetUrl(assetsBase, defaultImages[i], version));
      }
    }

    return result;
  }

  /**
   * Resolve images for a product with occasion fallback (async version)
   *
   * Strategy:
   * 1. Use occasion images from collections.{occasion}.product_slides.{product}
   * 2. Fill remaining slots with product images from products.{handle}.slides
   *
   * All images must be explicitly listed in content-map.json.
   *
   * @param {Object} map - The content map
   * @param {string} productHandle - Product handle
   * @param {string} occasion - Collection/occasion handle (optional)
   * @param {number} maxCount - Maximum images to return (default 6)
   * @param {string} assetsBase - Base URL for assets
   * @param {string} [version] - Cache-busting version string
   * @returns {Promise<string[]>} Array of image URLs
   */
  async function resolveImagesAsync(map, productHandle, occasion, maxCount = 6, assetsBase = '', version) {
    const productMeta = getProductMeta(map, productHandle);
    if (!productMeta) return [];

    const defaultImages = getImageFilenames(productMeta.slides || []);

    // If no occasion, return default images only
    if (!occasion) {
      return defaultImages
        .slice(0, maxCount)
        .map(f => buildAssetUrl(assetsBase, f, version));
    }

    // Get occasion-specific images for this product
    const occasionImages = getCollectionProductImages(map, occasion, productHandle);

    // Build result: occasion images first, then fill with defaults
    const result = [];
    const targetCount = Math.max(occasionImages.length, defaultImages.length);

    for (let i = 0; i < targetCount && result.length < maxCount; i++) {
      if (i < occasionImages.length && occasionImages[i]) {
        result.push(buildAssetUrl(assetsBase, occasionImages[i], version));
      } else if (i < defaultImages.length) {
        result.push(buildAssetUrl(assetsBase, defaultImages[i], version));
      }
    }

    return result;
  }

  /**
   * Check if a product supports occasion-specific images
   * @param {string} handle - Product handle
   * @returns {boolean}
   */
  function supportsOccasionImages(handle) {
    return handle === 'fridge-magnets' || handle === 'photo-magnets' || handle === 'save-the-date';
  }

  /**
   * Get text content (headline, subheading) for display
   * Prioritizes collection-specific text, falls back to product defaults
   * @param {Object} map - The content map
   * @param {string} productHandle - Product handle
   * @param {string} collectionHandle - Collection handle (optional)
   * @returns {Object} { headline, subheading, overlayHeading, overlaySubheading, titlePrefix }
   */
  function getDisplayText(map, productHandle, collectionHandle) {
    const productMeta = getProductMeta(map, productHandle);
    const collectionMeta = collectionHandle ? getCollectionMeta(map, collectionHandle) : null;

    return {
      headline: collectionMeta?.hero?.heading || productMeta?.title || '',
      subheading: collectionMeta?.hero?.subheading || productMeta?.copy?.functional || productMeta?.description || '',
      overlayHeading: collectionMeta?.hero?.overlay_heading || productMeta?.overlay_heading || productMeta?.title || '',
      overlaySubheading: collectionMeta?.hero?.overlay_subheading || productMeta?.overlay_subheading || productMeta?.copy?.emotional || productMeta?.description || '',
      titlePrefix: collectionMeta?.title_prefix || ''
    };
  }

  /**
   * Resolve slides for a product. Returns typed objects { type, url }.
   *
   * Resolution order:
   * 1. collections.{occasion}.product_slides.{handle} (occasion override, full slides array)
   * 2. products.{handle}.slides (product defaults)
   *
   * Each entry in the slides array is either:
   * - a string (image filename) -> { type: "image", url }
   * - an object { type: "video", src } -> { type: "video", url }
   *
   * @param {Object} map - The content map
   * @param {string} productHandle - Product handle
   * @param {string} occasion - Collection/occasion handle (optional)
   * @param {string} assetsBase - Base URL for assets
   * @param {string} [version] - Cache-busting version string
   * @returns {Array<{type: string, url: string}>} Array of slide objects
   */
  function resolveSlides(map, productHandle, occasion, assetsBase, version) {
    assetsBase = assetsBase || '';
    const productMeta = getProductMeta(map, productHandle);
    if (!productMeta) return [];

    // Choose source: occasion-specific overrides product defaults
    const useGeneric = map?.collections?.[occasion]?.use_generic_images;
    const occasionSlides = (occasion && !useGeneric)
      ? (map?.collections?.[occasion]?.product_slides?.[productHandle] || [])
      : [];

    const sourceSlides = occasionSlides.length > 0
      ? occasionSlides
      : (productMeta.slides || []);

    if (!Array.isArray(sourceSlides) || sourceSlides.length === 0) return [];

    const resolved = sourceSlides.map(entry => {
      if (typeof entry === 'string') {
        return { type: 'image', url: buildAssetUrl(assetsBase, entry, version) };
      }
      if (entry && typeof entry === 'object' && entry.type === 'video' && entry.src) {
        return { type: 'video', url: buildAssetUrl(assetsBase, entry.src, version) };
      }
      return null;
    }).filter(Boolean);

    // Auto-append generic size reference image (product-specific, not occasion-specific)
    const abbrev = productMeta.abbrev;
    if (abbrev) {
      resolved.push({ type: 'image', url: buildAssetUrl(assetsBase, `i-${abbrev}--size.jpg`, version) });
    }

    return resolved;
  }

  // Legacy alias for backward compatibility
  function getOccasionMeta(map, occasion) {
    return getCollectionMeta(map, occasion);
  }

  /**
   * Get styles array for a product (if defined).
   * Used by the PDP style picker to offer design/template choices
   * that swap the image carousel without affecting Shopify variants.
   * @param {Object} map - The content map
   * @param {string} handle - Product handle
   * @returns {Array|null} Styles array or null if not defined
   */
  function getProductStyles(map, handle) {
    const styles = map?.products?.[handle]?.styles;
    return Array.isArray(styles) && styles.length > 0 ? styles : null;
  }

  // Expose API globally for use by other scripts
  window.ProductImages = {
    loadMediaMap,
    getAbbrev,
    getProductMeta,
    getProductUrl,
    getCollectionMeta,
    getOccasionMeta, // Legacy alias
    getCollectionProductImages,
    getImageFilenames,
    resolveImages,
    resolveImagesAsync,
    resolveSlides,
    supportsOccasionImages,
    getDisplayText,
    getProductStyles,
    buildAssetUrl,
    buildOccasionImageFilename,
    buildGenericImageFilename,
    imageExists
  };

})();
